import { gql, useMutation } from '@apollo/client';
import { ModalActions, ModalContent, ModalLoadingButton, useModal } from 'components/ui/modal';
import { useToast } from 'components/ui/toast';
import { useEffect } from 'react';
// TODO: When testable, update deprecated MutationErrorText with ResponseHasErrors and HandleNoResponse pattern
import { Mutation, MutationUserSiteUserInvitationAcceptArgs } from 'middleware-types';
import { MutationErrorText } from 'utils/errors';
import { refreshToken, useSession } from 'utils/session';
import { Typography } from '@mui/material';

/* Mutation for accepting a site user invitation. */
export const ACCEPT_INVITATION = gql`
	mutation Accept_Invitation($invitationId: String!) {
		userSiteUserInvitationAccept(invitationId: $invitationId) {
			profileLastUpdatedUtc
			affiliation
			roles {
				id
				name
				isAdminRole
				permissionKeys
				siteRoleCategory {
					id
					name
				}
			}
		}
	}
`;

export const useAcceptInvitationMutation = (invitationId: string) => {
	const toast = useToast();
	const [onAcceptInvitation] = useMutation<
		Pick<Mutation, 'userSiteUserInvitationAccept'>,
		MutationUserSiteUserInvitationAcceptArgs
	>(ACCEPT_INVITATION, {
		variables: {
			invitationId: invitationId,
		},
	});

	const onAccept = async (userId: string) => {
		return await onAcceptInvitation({
			variables: {
				invitationId: invitationId,
			},
			update: async (cache) => {
				await refreshToken();
				cache.evict({
					id: 'ROOT_QUERY',
					fieldName: 'loginInformation',
				});
				cache.evict({
					id: `UserAccount:${userId}`,
				});
				cache.evict({
					id: `UserEmblem:${userId}`,
				});
				cache.evict({
					id: `UserQuery:{}`,
					fieldName: 'loginInformation',
				});
				cache.gc();
			},
		})
			.then(async (res) => {
				// TODO: When testable, update deprecated MutationErrorText with ResponseHasErrors and HandleNoResponse pattern
				if (res.errors) {
					console.log(res.errors);
					toast.push(MutationErrorText(res.errors), { variant: 'error' });
					return false;
				}
				toast.push('Thank you for acknowledging your Site User Invitation.', {
					variant: 'success',
				});
				return true;
			})
			.catch(() => {
				// TODO: When testable, update deprecated MutationErrorText with ResponseHasErrors and HandleNoResponse pattern
				toast.push(MutationErrorText(), { variant: 'error' });
				return false;
			});
	};

	return { onAccept };
};

/**
 * SiteUserAcknowledgement - a non-dismissable modal to confirm a site user invitation.
 * @returns SiteUserAcknowledgement
 */
export const useSiteUserAcknowledgement = () => {
	const { user } = useSession();
	const { showModal } = useModal();

	const { onAccept } = useAcceptInvitationMutation(user.siteUserInvitation?.invitationId ?? '');

	useEffect(() => {
		if (!user.siteUserInvitation) return;
		showModal({
			title: 'Site User Acknowledgement',
			hero: '/img/site-user-registration.svg',
			content: (
				<ModalContent>
					<Typography variant="body1">
						You have been invited into FileTrac Evolve as a Site-Level User, fulfilling
						the {user.siteUserInvitation?.role?.name} role!
					</Typography>
					<Typography variant="body1">
						Please carefully read the terms of service and other legal documents. Once
						you have read and understood the legal documents, you may accept your
						Site-Level User Invitation and assume the role by clicking “Acknowledge
						Invitation” below.
					</Typography>
				</ModalContent>
			),
			actions: (
				<ModalActions>
					<ModalLoadingButton
						variant="contained"
						color="primary"
						onClick={() => onAccept(user.userId)}>
						Acknowledge Invitation
					</ModalLoadingButton>
				</ModalActions>
			),
		});
	}, [user.siteUserInvitation]);
	return {};
};
